import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@interacta-shared/data-access-auth';
import {
    getRouteDataId,
    GoogleAnalyticsService,
    isPageView,
} from '@interacta-shared/data-access-common';
import {
    ConfigurationService,
    InstalledFeaturesService,
} from '@interacta-shared/data-access-configuration';
import {
    changeTheme,
    selectCurrentTheme,
    systemTheme,
    ThemeOption,
    userThemeMode,
} from '@interacta-shared/data-access-theme';
import { openProTip, TipService } from '@interacta-shared/feature-tip';
import { BreakpointService } from '@interacta-shared/ui';
import { ConfirmDialogService } from '@interacta-shared/ui-common';
import { isDefined } from '@interacta-shared/util';
import { HistoryRoutingService, Language } from '@interacta-shared/util-common';
import { CatalogsStateService } from '@interacta/data-access-catalog';
import { CommunitiesStateService } from '@interacta/data-access-community-tree';
import { CONVO_AI_ENABLED_ROUTES } from '@modules/ai-convo/constants';
import {
    AI_SIDEPANEL_EXPANDED_PX,
    AI_SIDEPANEL_MINWIDTH_PX,
} from '@modules/ai-shared/constants';
import { selectAIIsSidePanelOpen } from '@modules/ai-shared/store/ai-shared.selectors';
import {
    AppBaseRoute,
    customerFeedback,
    getIconMode,
    IconMode,
    login,
    notificationSettings,
    unsubscribe,
} from '@modules/app-routing/routes';
import { CarouselService } from '@modules/carousel/services/carousel.service';
import { TemporaryLinkErrorHandlerService } from '@modules/communities/services/temporary-link-error-handler.service';
import { SystemMessageService } from '@modules/core/services/system-message.service';
import { AppSelectors, AppState } from '@modules/core/store';
import { EProfileOrigin } from '@modules/profile/models/profile-origin.enum';
import { isSidebarShown } from '@modules/sidebar/models/sidebar.utils';
import { LinkDialogStateService } from '@modules/state/services/links-dialog-state.service';
import { StateService } from '@modules/state/services/state.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, of } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';

const noHeaderBaseRoutes: AppBaseRoute[] = [
    login,
    'error',
    unsubscribe,
    customerFeedback,
];

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AppComponent implements OnInit {
    private readonly authService = inject(AuthService);
    private readonly appState = inject(StateService);
    readonly linkDialogStateService = inject(LinkDialogStateService);
    readonly temporaryLinkErrorHandlerService = inject(
        TemporaryLinkErrorHandlerService,
    );
    readonly linksDialogState = this.appState.linksDialogState;
    private readonly historyRoutingService = inject(HistoryRoutingService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly translate = inject(TranslateService);
    private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
    private readonly configurationService = inject(ConfigurationService);
    private readonly store = inject(Store<AppState>);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly communitiesStateService = inject(CommunitiesStateService);
    private readonly catalogsStateService = inject(CatalogsStateService);
    private readonly carouselService = inject(CarouselService);
    private readonly systemMessageService = inject(SystemMessageService);
    private readonly tipService = inject(TipService);
    private readonly breakpointService = inject(BreakpointService);
    private readonly installedFeaturesService = inject(
        InstalledFeaturesService,
    );

    logo = this.configurationService.getCustomerLogoUrl();

    readonly currentUser = this.authService.currentUserData;
    readonly theme = this.store.selectSignal(selectCurrentTheme);
    readonly routeState = this.store.selectSignal(
        AppSelectors.selectRouteState,
    );
    readonly iconMode = computed<IconMode>(() => {
        const route = this.routeState();
        return route ? getIconMode(route.appRoute) : 'menu';
    });
    readonly showHeader = computed(() => {
        const route = this.routeState();
        return (
            isDefined(route) &&
            (!route.appBaseRoute ||
                !noHeaderBaseRoutes.includes(route.appBaseRoute))
        );
    });
    readonly showSidebar = computed(() => {
        const route = this.routeState();
        return (
            isDefined(route) &&
            !!route.appRoute &&
            isSidebarShown(route.appRoute)
        );
    });
    readonly canShowCarousel = this.carouselService.canShowCarousel;
    readonly isCarouselOpen = this.carouselService.isCarouselOpen;
    readonly isMd = computed(() => {
        return this.breakpointService.breakpointsSignal()?.isMd ?? false;
    });

    readonly leftMenuUI = toSignal(
        this.appState.uiState.leftMenuUI.onDataChange(),
    );

    readonly isAISidePanelOpen = this.store.selectSignal(
        selectAIIsSidePanelOpen,
    );

    private readonly aiConvoFeature =
        this.installedFeaturesService.getInstalledFeature('aiConvo');

    readonly aiConvoEnabled = computed(
        () =>
            CONVO_AI_ENABLED_ROUTES.includes(
                this.routeState()?.appRoute ?? '',
            ) &&
            (this.aiConvoFeature() ?? false),
    );
    readonly AI_SIDEPANEL_MINWIDTH_PX = AI_SIDEPANEL_MINWIDTH_PX;
    readonly AI_SIDEPANEL_EXPANDED_PX = AI_SIDEPANEL_EXPANDED_PX;
    readonly aiSidePanelWidth = signal(AI_SIDEPANEL_MINWIDTH_PX);
    readonly isAISidePanelResizing = signal(false);

    readonly isAppInfoDialogOpen = signal(false);

    ngOnInit(): void {
        this.historyRoutingService.loadRouting();

        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                mergeMap((_) => getRouteDataId(this.activatedRoute)),
            )
            .subscribe((id) => {
                if (isPageView(id)) {
                    this.googleAnalyticsService.sendPageView(id);
                } else {
                    console.warn(
                        `Google Analytics: unregistered page view "${id}"`,
                    );
                }
            });

        this.router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe(() => {
                if (
                    this.isCarouselOpen() ||
                    this.systemMessageService.isNoticeOpen()
                ) {
                    return;
                }
                // A tab whose panels are routes navigates on every activation:
                // moving the focus to the sentinel there would take it off the
                // tab strip, and the arrows would stop reaching the other tabs.
                if (document.activeElement?.closest('[role="tablist"]')) {
                    return;
                }
                document
                    .getElementById('navigation-focus-sentinel')
                    ?.focus({ preventScroll: true });
            });

        this.configurationService
            .checkUpdateVersionRequired()
            .pipe(filter((isNewVersion) => isNewVersion))
            .subscribe(() =>
                this.tipService.info('TIPS.NEW_APP_VERSION', 'reload'),
            );
    }

    changeTheme(mode: ThemeOption): void {
        this.store.dispatch(
            changeTheme({
                theme: mode === 'system' ? systemTheme() : userThemeMode(mode),
            }),
        );
    }

    changeLanguage(lang: Language): void {
        const currentLang = this.configurationService.currentLanguage();
        if (lang?.code !== currentLang.code) {
            this.translate
                .use(lang.code)
                .pipe(
                    switchMap((_) =>
                        this.confirmDialogService.open({
                            title: 'DIALOG_BASE.LABEL_TITLE_LANGUAGE_RELOAD',
                            description:
                                'DIALOG_BASE.LABEL_MESSAGE_LANGUAGE_RELOAD',
                        }),
                    ),
                    switchMap((confirm) =>
                        confirm
                            ? of(confirm)
                            : this.translate
                                  .use(currentLang.code)
                                  .pipe(switchMap(() => EMPTY)),
                    ),
                    switchMap((_) => [
                        this.communitiesStateService.clearCommunitiesCache(),
                        this.catalogsStateService.clearCatalogsCache(),
                    ]),
                )
                .subscribe(() => window.location.reload());
        }
    }

    logout(): void {
        this.authService.signOut().subscribe(() => {
            this.store.dispatch(
                openProTip({
                    title: 'SHARED.LOGOUT_TIP.TITLE',
                    message: 'SHARED.LOGOUT_TIP.MESSAGE',
                }),
            );
        });
    }

    openProfile(): void {
        const userId = this.currentUser()?.id;
        if (userId) {
            this.router.navigate([`/profile/${userId}`], {
                queryParams: { origin: EProfileOrigin.PEOPLE },
            });
        }
    }

    openNotificationsSettings(): void {
        this.router.navigate([notificationSettings]);
    }

    widthResized(width: number): void {
        this.aiSidePanelWidth.set(width);
    }
}
